import { useState, useEffect } from 'react';
import {
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  Banner
} from '@shopify/polaris';

/**
 * ActionItems component - Outcome-focused recommendations
 * Shows specific, actionable steps merchants can take to improve profit
 * This is NOT just analytics - each item has a clear action to take
 */
export default function ActionItems({ shop, stats, products, shopData, onNavigate, showToast }) {
  const [items, setItems] = useState([]);
  const [dismissedItems, setDismissedItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('profitpulse_dismissed_actions') || '[]');
    } catch {
      return [];
    }
  });

  useEffect(() => {
    const generated = generateActionItems(stats, products, shopData);
    const filtered = generated.filter(item => !dismissedItems.includes(item.id));
    setItems(filtered);
  }, [stats, products, shopData, dismissedItems]);

  const handleDismiss = (itemId) => {
    const updated = [...dismissedItems, itemId];
    setDismissedItems(updated);
    localStorage.setItem('profitpulse_dismissed_actions', JSON.stringify(updated));
  };

  if (items.length === 0) {
    return (
      <Card>
        <BlockStack gap="200">
          <Text variant="headingMd" as="h2">6. Recommendations to increase profit</Text>
          <Banner tone="success">
            <p>No urgent profit leaks are active right now. Your recommendations will appear here as new margin risks show up.</p>
          </Banner>
        </BlockStack>
      </Card>
    );
  }

  return (
    <Card>
      <BlockStack gap="400">
        <InlineStack align="space-between">
          <Text variant="headingMd" as="h2">6. Recommendations to increase profit</Text>
          <Badge tone="warning">{items.length} actions</Badge>
        </InlineStack>

        {items.slice(0, 4).map((item) => (
          <Card key={item.id} background={item.priority === 'high' ? 'bg-surface-critical' : 'bg-surface-secondary'}>
            <BlockStack gap="200">
              <InlineStack align="space-between" blockAlign="start">
                <InlineStack gap="200" blockAlign="center">
                  <Badge tone={item.priority === 'high' ? 'critical' : item.priority === 'medium' ? 'warning' : 'info'}>
                    {item.priority === 'high' ? 'Urgent' : item.priority === 'medium' ? 'Important' : 'Tip'}
                  </Badge>
                  <Text variant="headingSm" as="h3">{item.title}</Text>
                </InlineStack>
                <Button variant="plain" onClick={() => handleDismiss(item.id)} size="slim">
                  Dismiss
                </Button>
              </InlineStack>
              <Text variant="bodyMd" as="p">{item.description}</Text>
              {item.impact && (
                <Text variant="bodySm" as="p" tone="success">
                  Potential impact: {item.impact}
                </Text>
              )}
              {item.action && (
                <InlineStack gap="200">
                  <Button size="slim" onClick={() => {
                    if (item.actionPage) {
                      onNavigate(item.actionPage);
                    }
                  }}>
                    {item.action}
                  </Button>
                </InlineStack>
              )}
            </BlockStack>
          </Card>
        ))}
      </BlockStack>
    </Card>
  );
}

function generateActionItems(stats, products, shopData) {
  const settings = {
    alert_margin_threshold: 20,
    discount_margin_buffer_percent: 10,
    shipping_cost_alert_percent: 12,
    profit_target_percent: 25,
    ...(shopData?.settings || {})
  };
  const items = [];

  if (!stats && !products) return items;

  // 1. Missing product costs
  if (products && products.length > 0) {
    const missingCosts = products.filter(p => !p.cost);
    if (missingCosts.length > 0) {
      const topMissing = missingCosts.sort((a, b) => parseFloat(b.price) - parseFloat(a.price))[0];
      items.push({
        id: 'missing-costs',
        priority: missingCosts.length > 5 ? 'high' : 'medium',
        title: `${missingCosts.length} product(s) still missing costs`,
        description: `Without product costs, true profit per order is incomplete. "${topMissing?.productTitle}" is selling at $${topMissing?.price} without COGS coverage.`,
        impact: `Accurate margin protection for ${missingCosts.length} products`,
        action: 'Add Costs Now',
        actionPage: 'products'
      });
    }
  }

  // 2. Low-margin products
  if (products && products.length > 0) {
    const threshold = parseFloat(settings.alert_margin_threshold || 20);
    const lowMarginProducts = products
      .filter(p => p.margin !== null && p.margin !== undefined && p.margin < threshold)
      .sort((a, b) => a.margin - b.margin);

    if (lowMarginProducts.length > 0) {
      const worstProduct = lowMarginProducts[0];
      items.push({
        id: 'low-margin-products',
        priority: lowMarginProducts.length >= 3 ? 'high' : 'medium',
        title: `${lowMarginProducts.length} product(s) below ${threshold}% margin`,
        description: `"${worstProduct.productTitle}" is only ${worstProduct.margin.toFixed(1)}% margin. Reprice or reduce costs before more low-profit orders stack up.`,
        impact: 'Protect future orders before they turn negative',
        action: 'Review Products',
        actionPage: 'products'
      });
    }
  }

  // 3. Negative margin orders
  if (stats) {
    const negCount = parseInt(stats.negative_margin_count || 0);
    const negativeLoss = parseFloat(stats.negative_margin_loss || 0);
    if (negCount > 0) {
      items.push({
        id: 'negative-margins',
        priority: 'high',
        title: `${negCount} order(s) are already below zero`,
        description: `These orders have negative margins after COGS, shipping, fees, and discounts. Investigate which products, discount rules, or shipping costs caused the leak.`,
        impact: negativeLoss > 0
          ? `Recover up to ${negativeLoss.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} in recurring losses`
          : `Stop losing money on ${negCount} orders`,
        action: 'Review Orders',
        actionPage: 'orders'
      });
    }
  }

  // 4. Discount leakage
  if (stats) {
    const breachCount = parseInt(stats.discount_buffer_breach_count || 0);
    const breachAmount = parseFloat(stats.discount_buffer_breach_amount || 0);
    if (breachCount > 0) {
      items.push({
        id: 'discount-leakage',
        priority: 'medium',
        title: 'Discounts are eroding your margin buffer',
        description: `${breachCount} recent orders crossed your ${settings.discount_margin_buffer_percent}% discount buffer. Tighten code usage or exclude your thinnest-margin products.`,
        impact: breachAmount > 0
          ? `${breachAmount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} in discounted revenue crossed the buffer`
          : 'Protect future profit before discounting compounds',
        action: 'Review Orders',
        actionPage: 'orders'
      });
    }
  }

  // 5. Profit goal tracking
  if (shopData && !shopData.settings?.profit_target_percent) {
    items.push({
      id: 'no-target',
      priority: 'medium',
      title: 'Set your profit goal',
      description: 'A profit goal gives Profit Pulse AI a target to protect, so you can see how far your current net margin is from your goal.',
      action: 'Set Goal',
      actionPage: 'settings'
    });
  }

  if (stats) {
    const targetMargin = parseFloat(settings.profit_target_percent || 25);
    const netMargin = parseFloat(stats.net_margin ?? stats.avg_margin ?? 0);
    const revenue = parseFloat(stats.total_revenue || 0);
    if (revenue > 0 && netMargin < targetMargin) {
      const gapValue = revenue * ((targetMargin - netMargin) / 100);
      items.push({
        id: 'goal-gap',
        priority: netMargin < 0 ? 'high' : 'medium',
        title: `${(targetMargin - netMargin).toFixed(1)}% below your profit goal`,
        description: `Your current net margin is ${netMargin.toFixed(1)}% against a ${targetMargin}% goal. Focus on the lowest-margin products and the orders leaking profit first.`,
        impact: `Closing the gap is worth about ${gapValue.toLocaleString('en-US', { style: 'currency', currency: 'USD' })} at current revenue`,
        action: 'Open Goal Tracking',
        actionPage: 'settings'
      });
    }
  }

  // 6. Trial ending soon
  if (shopData?.trial_ends_at) {
    const daysLeft = Math.ceil((new Date(shopData.trial_ends_at) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysLeft > 0 && daysLeft <= 5) {
      items.push({
        id: 'trial-ending',
        priority: 'high',
        title: `Trial ends in ${daysLeft} day(s)`,
        description: 'Upgrade to keep margin protection alerts, recommendations, and longer order history active.',
        action: 'Choose a Plan',
        actionPage: 'settings'
      });
    }
  }

  // 7. High shipping costs
  if (stats) {
    const revenue = parseFloat(stats.total_revenue || 0);
    const shipping = parseFloat(stats.total_shipping || 0);
    if (revenue > 0 && shipping > 0 && (shipping / revenue) > (parseFloat(settings.shipping_cost_alert_percent || 12) / 100)) {
      items.push({
        id: 'high-shipping',
        priority: 'medium',
        title: 'Shipping costs are eating your margin',
        description: `Shipping is ${((shipping / revenue) * 100).toFixed(1)}% of revenue, above your ${settings.shipping_cost_alert_percent}% alert rule. Review thresholds, packaging, or carrier rates.`,
        impact: `Reducing shipping by 20% could save ${(shipping * 0.20).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}/month`
      });
    }
  }

  return items.sort((a, b) => {
    const priority = { high: 0, medium: 1, low: 2 };
    return (priority[a.priority] || 2) - (priority[b.priority] || 2);
  });
}
